import { z } from "zod";

const chatTokenUsageBucketSchema = z.object({
  key: z.enum([
    "prompt",
    "context",
    "knowledge",
    "reasoning",
    "completion",
    "other",
  ]),
  tokens: z.number(),
});

const chatTokenUsageTotalsSchema = z.object({
  inputTokens: z.number(),
  outputTokens: z.number(),
  reasoningTokens: z.number(),
  totalTokens: z.number(),
  totalCost: z.number().nullable(),
  isCostPartial: z.boolean(),
  recordsMissingTokens: z.number(),
});

const enforcedAllowanceItemSchema = z.object({
  scope: z.enum(["organization", "project", "personal", "user"]),
  usageKind: z.enum(["total", "agent"]),
  unit: z.enum(["tokens", "usd"]),
  limitAmount: z.number(),
  usedAmount: z.number(),
  remainingAmount: z.number(),
});

export const enforcedAllowanceSchema = z.object({
  isEnforcementEnabled: z.boolean(),
  period: z.enum(["week", "month", "quarter", "half_year", "year"]).nullable(),
  resetAt: z.string().nullable(),
  items: z.array(enforcedAllowanceItemSchema),
});

const myChatTokenUsageTurnSchema = z.object({
  assistantMessageUuid: z.string().nullable(),
  preview: z.string().nullable(),
  startedAt: z.string().nullable(),
  modelName: z.string().nullable(),
  modelTitle: z.string().nullable(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  reasoningTokens: z.number(),
  totalTokens: z.number(),
  totalCost: z.number().nullable(),
  isCostPartial: z.boolean(),
  buckets: z.array(chatTokenUsageBucketSchema),
});

export const myChatTokenUsageSchema = z.object({
  chatUuid: z.string(),
  currency: z.literal("USD"),
  summary: chatTokenUsageTotalsSchema,
  turns: z.array(myChatTokenUsageTurnSchema),
  allowance: enforcedAllowanceSchema,
});

const myTokenUsageAggregateGroupSchema = z.object({
  key: z.string().nullable(),
  label: z.string().nullable(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  reasoningTokens: z.number(),
  totalTokens: z.number(),
  totalCost: z.number().nullable(),
});

export const myTokenUsageAggregateSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
  currency: z.literal("USD"),
  summary: chatTokenUsageTotalsSchema,
  byModel: z.array(myTokenUsageAggregateGroupSchema),
  byChat: z.array(myTokenUsageAggregateGroupSchema),
  isByModelTruncated: z.boolean(),
  isByChatTruncated: z.boolean(),
  allowance: enforcedAllowanceSchema,
});

export type TChatTokenUsageBucket = z.infer<typeof chatTokenUsageBucketSchema>;
export type TChatTokenUsageTotals = z.infer<typeof chatTokenUsageTotalsSchema>;
export type TEnforcedAllowanceItem = z.infer<
  typeof enforcedAllowanceItemSchema
>;
export type TEnforcedAllowance = z.infer<typeof enforcedAllowanceSchema>;
export type TMyChatTokenUsageTurn = z.infer<typeof myChatTokenUsageTurnSchema>;
export type TMyChatTokenUsageResponse = z.infer<typeof myChatTokenUsageSchema>;
export type TMyTokenUsageAggregateGroup = z.infer<
  typeof myTokenUsageAggregateGroupSchema
>;
export type TMyTokenUsageAggregateResponse = z.infer<
  typeof myTokenUsageAggregateSchema
>;
export type TChatTokenUsageBucketKey = TChatTokenUsageBucket["key"];
